import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { db } from '../../../firebaseConfig';
import {
    doc,
    getDoc,
    collection,
    query,
    where,
    getDocs,
    Timestamp,
    limit,
    updateDoc
} from 'firebase/firestore';
import GradientSubmitButton from '../../GradientSubmitButton';
import Toast from '../../Toast';
import { LeadData } from '../../../pages/Clients/Admin/Function/LeadFunctions';

// Types
interface User {
    id: string;
    name: string;
}

interface CollaborateModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedStudent: LeadData | null;
    selectedStudentPhone: string;
    onSuccess?: (message: string) => void;
}

const CollaborateModal: React.FC<CollaborateModalProps> = ({
    isOpen,
    onClose,
    selectedStudent,
    selectedStudentPhone,
    onSuccess
}) => {
    const { userData, currentUser } = useAuth();

    // State variables
    const [collaborators, setCollaborators] = useState<string[]>([]);
    const [selectedUserDetails, setSelectedUserDetails] = useState<User[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<User[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isError, setIsError] = useState(false);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setSearchResults([]);
            setCollaborators([]);
            setSelectedUserDetails([]);
            setSubmitLoading(false);
        }
    }, [isOpen]);

    // Fetch existing collaborators when modal opens
    useEffect(() => {
        if (isOpen && selectedStudent && userData?.companyId) {
            fetchExistingCollaborators(selectedStudent);
            fetchAvailableUsers();
        }
    }, [isOpen, selectedStudent, userData?.companyId]);

    const fetchExistingCollaborators = async (student: LeadData) => {
        if (!userData?.companyId) return;

        try {
            // First, get the lead document to get collaborator IDs
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, student.id);
            const leadDoc = await getDoc(leadRef);
            if (leadDoc.exists()) {
                const leadData = leadDoc.data();
                const existingCollaborators = leadData.collaborators || [];
                setCollaborators(existingCollaborators);

                // Then, fetch user details for each collaborator
                if (existingCollaborators.length > 0) {
                    const userPromises = existingCollaborators.map(async (userId: string) => {
                        const userDoc = await getDoc(doc(db, 'users', userId));
                        if (userDoc.exists()) {
                            return {
                                id: userDoc.id,
                                name: userDoc.data().name
                            };
                        }
                        return null;
                    });

                    const users = await Promise.all(userPromises);
                    const validUsers = users.filter((user): user is User => user !== null);
                    setSelectedUserDetails(validUsers);
                }
            }
        } catch (error) {
            console.error('Error fetching existing collaborators:', error);
        }
    };

    const handleSearchUsers = async (value: string) => {
        if (!userData?.companyId || value.length < 3) {
            setSearchResults([]);
            return;
        }

        setIsLoadingUsers(true);
        try {
            const upperValue = value.toUpperCase();
            const usersRef = collection(db, 'users');
            const q = query(
                usersRef,
                where('companyId', '==', userData.companyId),
                where('name', '>=', upperValue),
                where('name', '<=', upperValue + '\uf8ff'),  
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const usersData = querySnapshot.docs
                .filter(doc => doc.data().isActive !== false) // Filter out deactivated users
                .map(doc => ({
                    id: doc.id,
                    name: doc.data().name
                }));

            setSearchResults(usersData);
        } catch (error) {
            console.error('Error searching users:', error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleSelectUser = async (userId: string) => {
        if (!collaborators.includes(userId)) {
            // Add to collaborators array
            setCollaborators([...collaborators, userId]);

            // Add to selected user details
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setSelectedUserDetails(prev => [...prev, {
                    id: userId,
                    name: userData.name
                }]);
            }
        }
    };

    const handleRemoveUser = (userId: string) => {
        setCollaborators(collaborators.filter(id => id !== userId));
        setSelectedUserDetails(prev => prev.filter(user => user.id !== userId));
    };

    // Fetch available users with same companyId
    const fetchAvailableUsers = async () => {
        if (!userData?.companyId) return;

        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('companyId', '==', userData.companyId));
            const querySnapshot = await getDocs(q);

            const usersData = querySnapshot.docs
                .filter(doc => doc.data().isActive !== false) // Filter out deactivated users
                .map(doc => ({
                    id: doc.id,
                    name: doc.data().name
                }));

            setSearchResults(usersData);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleCollaborateSubmit = async () => {
        if (!selectedStudent || !userData?.companyId) return;

        setSubmitLoading(true);
        try {
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, selectedStudent.id);
            await updateDoc(leadRef, {
                collaborators: collaborators,
                collaborateDate: Timestamp.fromDate(new Date()),
                updatedBy: currentUser?.uid,
                updatedAt: Timestamp.fromDate(new Date()),
            });

            const successMessage = 'Collaborators added successfully!';
            setToastMessage(successMessage);
            setShowToast(true);
            setIsError(false);

            // Reset state and close modal
            setTimeout(() => {
                setShowToast(false);
                onClose();
                if (onSuccess) {
                    onSuccess(successMessage);
                }
                window.location.reload();
            }, 2000);

        } catch (error) {
            const errorMessage = 'Failed to add collaborators. Please try again.';
            setToastMessage(errorMessage);
            setShowToast(true);
            setIsError(true);
            console.error('Error updating collaborators:', error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleCancel = () => {
        setCollaborators([]);
        setSelectedUserDetails([]);
        setSearchTerm('');
        setSearchResults([]);
        onClose();
    };

    if (!isOpen || !selectedStudent) {
        return null;
    }

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-96">
                    <h2 className="text-lg font-semibold mb-4">Add Collaborators</h2>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Student Name</label>
                        <div className="mt-1 p-2 bg-gray-50 rounded-md">
                            {selectedStudent.name}
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                        <div className="mt-1 p-2 bg-gray-50 rounded-md">
                            {selectedStudentPhone}
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Select Collaborators</label>
                        <div className="relative mt-1">
                            <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                                placeholder="Type 3 or more characters to search users..."
                                value={searchTerm}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSearchTerm(value);
                                    handleSearchUsers(value);
                                }}
                            />
                            {isLoadingUsers && (
                                <div className="absolute right-2 top-2">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-600"></div>
                                </div>
                            )}

                            {/* Dropdown of search results */}
                            {searchTerm.length >= 3 && searchResults.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                                    {searchResults
                                        .filter(user => !collaborators.includes(user.id))
                                        .map(user => (
                                            <div
                                                key={user.id}
                                                className="cursor-pointer hover:bg-gray-100 px-4 py-2"
                                                onClick={() => {
                                                    handleSelectUser(user.id);
                                                    setSearchTerm('');
                                                }}
                                            >
                                                {user.name}
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>

                        {/* Selected users display */}
                        <div className="mt-2 flex flex-wrap gap-2">
                            {selectedUserDetails.map(user => (
                                <div
                                    key={user.id}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                                >
                                    {user.name}
                                    <button
                                        type="button"
                                        className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-cyan-600 hover:bg-cyan-200 hover:text-cyan-500 focus:outline-none"
                                        onClick={() => handleRemoveUser(user.id)}
                                    >
                                        <span className="sr-only">Remove {user.name}</span>
                                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={handleCancel}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            disabled={submitLoading}
                        >
                            Cancel
                        </button>

                        <GradientSubmitButton
                            onClick={handleCollaborateSubmit}
                            label="Submit"
                            loadingLabel="Submitting..."
                            loading={submitLoading}
                        />
                    </div>
                </div>
            </div>

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={isError}
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </>
    );
};

export default CollaborateModal;
